import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfile, updatePassword } from '../../redux/authSlice';
import { 
  User, Lock, ShieldCheck, Mail, Save, AlertCircle, 
  Activity, Fingerprint, Shield, Zap, Key
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import PageHeader from '../../components/PageHeader';

export default function SuperAdminProfile() {
  const dispatch = useDispatch();
  const { user, loading } = useSelector(state => state.auth);

  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    companyName: user?.companyName || '',
    email: user?.email || '',
  });
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });

  useEffect(() => {
    if (user) {
      setProfileData({
        companyName: user.companyName || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    if (e) e.preventDefault();
    try {
      await dispatch(updateProfile({ companyName: profileData.companyName })).unwrap();
      alertService.toast('success', 'Administrative identity synchronized');
    } catch (err) {
      alertService.error('Update Failed', err || 'Could not update profile info.');
    }
  };

  const handlePasswordUpdate = async (e) => {
    if (e) e.preventDefault();
    if (passwords.next !== passwords.confirm) {
      alertService.error('Validation Error', 'New passwords do not match.');
      return;
    }
    try {
      await dispatch(updatePassword({ currentPassword: passwords.current, newPassword: passwords.next })).unwrap();
      alertService.toast('success', 'Security credentials updated');
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (err) {
      alertService.error('Update Error', err || 'Verification of current credentials failed.');
    }
  };

  const TABS = [
    { id: 'profile', label: 'Profile Identity', icon: User, desc: 'Admin Branding' },
    { id: 'security', label: 'Access Security', icon: Lock, desc: 'Authentication' },
  ];

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader title="My Profile" icon={User} subtitle="Manage your administrator identity and security" />

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Navigation Matrix */}
        <div className="w-full lg:w-64 space-y-6 shrink-0">
          <Card className="p-2 bg-white border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] rounded-lg">
            <div className="px-4 py-3 mb-1 text-[11px] font-semibold text-[#706E6B] uppercase border-b border-[#D8DDE6]">
              Profile Sector
            </div>
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={cn(
 "w-full flex items-center gap-3 px-4 h-9 rounded-sm transition-colors text-left",
 activeTab === t.id 
 ? "bg-[#E0F2FE] text-[#0176D3] font-semibold" 
 : "text-[#444444] hover:bg-[#F3F2F2] hover:text-[#080707] font-semibold"
 )}
              >
                <t.icon size={16} className={cn(activeTab === t.id ? "text-[#0176D3]" : "text-[#706E6B]")} />
                <span className="text-[13px] flex-1">{t.label}</span>
              </button>
            ))}
          </Card>

          <Card className="bg-[#FFF5F5] border-[#FC8181] p-4 rounded-lg">
             <div className="flex items-center gap-2 text-[12px] font-bold text-[#C53030] mb-2">
                <ShieldCheck size={16} /> Security Advisory
             </div>
             <p className="text-[12px] text-[#C53030] leading-relaxed">Identity parameters are hard-coded into the neural registry. Ensure high-entropy credentials for platform stability.</p>
          </Card>
        </div>

        {/* Configuration Node */}
        <div className="flex-1 min-w-0 w-full space-y-8">
          {activeTab === 'profile' && (
            <Card padding="p-0" className="overflow-hidden shadow-sm bg-white border-[#D8DDE6] rounded-md">
              <div className="px-6 py-5 border-b border-[#D8DDE6] bg-[#F4F6F9] flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-4">
                   <div className="w-12 h-12 rounded-md flex items-center justify-center bg-[#E0F2FE] text-[#0176D3] border border-[#BAE6FD]">
                      <User size={24} />
                   </div>
                   <div>
                      <h3 className="text-lg font-bold text-[#080707] leading-tight">Identity Parameters</h3>
                      <div className="flex items-center gap-2 mt-1">
                         <div className="w-2 h-2 rounded-full bg-[#16A34A]" />
                         <span className="text-[12px] font-semibold text-[#706E6B] uppercase ">Configuration</span>
                      </div>
                   </div>
                </div>
              </div>

              <form onSubmit={handleProfileUpdate} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[#444444]">Official Identification</label>
                    <Input
                      value={profileData.companyName}
                      onChange={e => setProfileData({...profileData, companyName: e.target.value})}
                      placeholder="Enter administrator name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[#444444]">Network Endpoint</label>
                    <Input 
                      value={profileData.email} 
                      disabled 
                      className="bg-[#F3F2F2] text-[#706E6B]"
                      placeholder="identity@endpoint.cli"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-md bg-[#E0F2FE] border border-[#BAE6FD] flex items-start gap-3">
                  <div className="mt-0.5 text-[#0176D3]">
                    <ShieldCheck size={16} />
                  </div>
                  <p className="text-[13px] text-[#014486] leading-relaxed">
                    Synchronized identity is utilized for high-latency platform audit trails and compliance authorization.
                  </p>
                </div>

                <div className="pt-6 border-t border-[#D8DDE6] flex justify-end">
                  <button type="submit" disabled={loading} className="h-9 px-6 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-colors flex items-center gap-2">
                    <Save size={16} /> Preserve Identity
                  </button>
                </div>
              </form>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card padding="p-0" className="overflow-hidden shadow-sm bg-white border-[#D8DDE6] rounded-md">
              <div className="px-6 py-5 border-b border-[#D8DDE6] bg-[#F4F6F9] flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-4">
                   <div className="w-12 h-12 rounded-md flex items-center justify-center bg-[#FFF5F5] text-[#C53030] border border-[#FC8181]">
                      <Lock size={24} />
                   </div>
                   <div>
                      <h3 className="text-lg font-bold text-[#080707] leading-tight">Password</h3>
                      <div className="flex items-center gap-2 mt-1">
                         <div className="w-2 h-2 rounded-full bg-[#E53E3E]" />
                         <span className="text-[12px] font-semibold text-[#706E6B] uppercase ">Access Authority Sector</span>
                      </div>
                   </div>
                </div>
              </div>

              <form onSubmit={handlePasswordUpdate} className="p-6 space-y-6">
                <div className="max-w-md space-y-1.5">
                  <label className="text-[12px] font-semibold text-[#444444]">Current Verification Key</label>
                  <Input 
                    type="password" 
                    value={passwords.current} 
                    onChange={e => setPasswords({...passwords, current: e.target.value})} 
                    placeholder="••••••••••••"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[#444444]">New Access Sequence</label>
                    <Input 
                      type="password" 
                      value={passwords.next} 
                      onChange={e => setPasswords({...passwords, next: e.target.value})} 
                      placeholder="••••••••••••"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[#444444]">Confirm Identity Key</label>
                    <Input 
                      type="password" 
                      value={passwords.confirm} 
                      onChange={e => setPasswords({...passwords, confirm: e.target.value})} 
                      placeholder="••••••••••••"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-md bg-[#FFFFF0] border border-[#F6E05E] flex items-start gap-3">
                  <div className="mt-0.5 text-[#B7791F]">
                    <Zap size={16} />
                  </div>
                  <p className="text-[13px] text-[#975A16] leading-relaxed">
                    A synchronization shift in credentials will instantly terminate existing authoritative tokens across all active system nodes.
                  </p>
                </div>

                <div className="pt-6 border-t border-[#D8DDE6] flex justify-end">
                  <button type="submit" disabled={loading} className="h-9 px-6 rounded whitespace-nowrap bg-[#080707] hover:bg-[#444444] text-white text-[13px] font-semibold transition-colors flex items-center gap-2">
                    <ShieldCheck size={16} /> Save
                  </button>
                </div>
              </form>
            </Card>
          )}

          {/* Authority Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: 'Role Authority', val: 'Super Admin', icon: Shield, color: 'text-[#805AD5]', bg: 'bg-[#FAF5FF]' },
              { label: 'Account Tier', val: 'Primary Control', icon: Fingerprint, color: 'text-[#16A34A]', bg: 'bg-[#DCFCE7]' },
              { label: 'Platform Status', val: 'Operational', icon: Activity, color: 'text-[#0176D3]', bg: 'bg-[#E0F2FE]' }
            ].map((m, i) => (
              <Card key={i} className="p-5 flex items-center gap-4 bg-white border-[#D8DDE6] rounded-md shadow-sm">
                <div className={cn("w-12 h-12 rounded-md flex items-center justify-center", m.bg, m.color)}>
                  <m.icon size={20} />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-[#706E6B] uppercase ">{m.label}</div>
                  <div className="text-[14px] font-bold text-[#080707]">{m.val}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
